import { subject } from "@casl/ability";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
  serverTimestamp,
  getCountFromServer
} from "firebase/firestore";
import { Asserts } from "yup";
import { firestore } from "@/base";
import { getActiveLicenciadaDocumentReference } from "@/modules/licenciadas/firestore";
import { getCurrentUser } from "@/services/auth";
import { generateQuery } from "@/utils/firestore";
import embaixadorPessoaSchema from "./schema/embaixador-pessoa-schema";

export type TEmbaixadorPessoaForm = Asserts<typeof embaixadorPessoaSchema>;

export const EMBAIXADORES_PESSOAS_COLLECTION_KEY = ["embaixadores-pessoas"] as const;

const embaixadorPessoaConverter = {
  toFirestore(data: TEmbaixadorPessoa) {
    const { id, ...rest } = data;
    return rest;
  },
  fromFirestore(snap: any) {
    const data: TEmbaixadorPessoa = {
      id: snap.id,
      ...snap.data()
    };
    return subject("TEmbaixadorPessoa", data);
  }
};

const getEmbaixadoresPessoasCollection = (empresaId: string, withConverter: boolean = true) => {
  const licenciadaDocumentReference = getActiveLicenciadaDocumentReference();
  let colReference;

  if (licenciadaDocumentReference) {
    const empresaDocumentReference = doc(licenciadaDocumentReference, "empresas", empresaId);
    colReference = collection(empresaDocumentReference, "embaixadoresPessoas");
  } else {
    colReference = collection(firestore, "empresas", empresaId, "embaixadoresPessoas");
  }

  return withConverter ? colReference.withConverter(embaixadorPessoaConverter) : colReference;
};

const DEFAULT_LIMIT = 10;
const BASE_FILTER = [{ id: "deletedAt", value: ["==", null] }];

export const getEmbaixadoresPessoas = (
  empresaId: string,
  {
    sorting = [],
    filters = [],
    pagination = { pageIndex: 0, pageSize: DEFAULT_LIMIT },
    paginationCursors
  }: TPaginatedQueryOptions<TEmbaixadorPessoa> = {}
): Promise<TPaginatedQueryResponse<TEmbaixadorPessoa>> => {
  const embaixadoresCollectionReference = getEmbaixadoresPessoasCollection(empresaId, false).withConverter(
    embaixadorPessoaConverter
  );

  const allFilters = [...BASE_FILTER, ...filters];
  const allSorting = sorting.length > 0 ? sorting : [{ id: "nome", desc: false }];

  const qAll = generateQuery(embaixadoresCollectionReference, { filters: allFilters });
  const qPaginated = generateQuery(embaixadoresCollectionReference, {
    sorting: allSorting,
    filters: allFilters,
    pagination,
    paginationCursors
  });

  return Promise.all([getDocs(qPaginated), getCountFromServer(qAll)]);
};

export const addEmbaixadorPessoa = async (empresaId: string, embaixadorPessoa: TEmbaixadorPessoaForm) => {
  const user = getCurrentUser();
  if (!user) throw new Error("Usuário não autenticado");

  const collectionReference = getEmbaixadoresPessoasCollection(empresaId, false);

  const documentReference = await addDoc(collectionReference, {
    ...embaixadorPessoa,
    empresaId,
    createdAt: serverTimestamp(),
    createdBy: user.uid,
    deletedAt: null
  });

  return documentReference.id;
};

export const updateEmbaixadorPessoa = async (
  empresaId: string,
  embaixadorPessoaId: string,
  embaixadorPessoa: TEmbaixadorPessoaForm
) => {
  const user = getCurrentUser();
  if (!user) throw new Error("Usuário não autenticado");

  const documentReference = doc(getEmbaixadoresPessoasCollection(empresaId, false), embaixadorPessoaId);

  await updateDoc(documentReference, {
    ...embaixadorPessoa,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
};

export const checkEmailExists = async (empresaId: string, email: string, excludeId?: string): Promise<boolean> => {
  if (!email) return false;

  const collectionReference = getEmbaixadoresPessoasCollection(empresaId, false);

  const constraints = [where("deletedAt", "==", null), where("email", "==", email)];

  const q = query(collectionReference, ...constraints);
  const snapshot = await getDocs(q);

  if (excludeId) {
    return snapshot.docs.some((document_) => document_.id !== excludeId);
  }

  return !snapshot.empty;
};

export const deleteEmbaixadorPessoa = async (empresaId: string, embaixadorPessoaId: string) => {
  const user = getCurrentUser();
  if (!user) throw new Error("Usuário não autenticado");

  const documentReference = doc(getEmbaixadoresPessoasCollection(empresaId, false), embaixadorPessoaId);

  await updateDoc(documentReference, {
    deletedAt: serverTimestamp(),
    updatedBy: user.uid
  });
};

export const getProjetosByEmbaixadorPessoa = async (
  empresaId: string,
  embaixadorPessoaId: string
): Promise<TProjeto[]> => {
  const licenciadaDocumentReference = getActiveLicenciadaDocumentReference();
  let projetosCollectionReference;

  if (licenciadaDocumentReference) {
    const empresaDocumentReference = doc(licenciadaDocumentReference, "empresas", empresaId);
    projetosCollectionReference = collection(empresaDocumentReference, "projetos");
  } else {
    projetosCollectionReference = collection(firestore, "empresas", empresaId, "projetos");
  }

  const q = query(
    projetosCollectionReference,
    where("deletedAt", "==", null),
    where("embaixadoresPessoas", "array-contains", embaixadorPessoaId)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((document_) => ({
    id: document_.id,
    ...document_.data()
  })) as TProjeto[];
};
